import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, Button, IconButton,
  Divider, Snackbar, Alert,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../contexts/AuthContext';

// DEPARTURE POPUP (Oct 2026) — admins/managers only, on EVERY page: when a
// truck's scheduled time passes without a Mark departed, this polls
// /api/assignments/departure-popup (30s) and raises a modal the user must
// act on (Mark departed / Snooze / Dismiss — no backdrop close). Snooze and
// dismiss are stored SERVER-SIDE per user (survive refresh, apply across
// tabs); Mark departed (or the auto-depart grace sweep) clears it for all.
// Polling uses PLAIN fetch and fails quietly — a background feature must
// never log the user out or block the page they're working on.

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const timeET = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }) : '—');
const keyOf = (d) => `${d.platform}|${d.scheduled_at}`;

export default function DeparturePopup() {
  const { currentUser } = useAuth();
  const isLead = currentUser && ['manager', 'admin'].includes(currentUser.role);
  const [due, setDue] = useState([]);
  const [defaultSnooze, setDefaultSnooze] = useState(15);
  const [snoozeMin, setSnoozeMin] = useState({}); // key -> minutes chosen in the stepper
  const [busy, setBusy] = useState(null);         // key of the row an action is running on
  const [error, setError] = useState('');
  const seeded = useRef(new Set());               // keys whose stepper got the default already

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/assignments/departure-popup`, { headers: authHeaders() });
      if (!res.ok) return; // quiet — never disturb the page from a background poll
      const data = await res.json();
      if (!data || data.enabled === false) { setDue([]); return; }
      setDefaultSnooze(data.default_snooze_minutes || 15);
      setDue(data.due || []);
      setSnoozeMin((prev) => {
        const next = { ...prev };
        for (const d of data.due || []) {
          const k = keyOf(d);
          if (!seeded.current.has(k)) { next[k] = data.default_snooze_minutes || 15; seeded.current.add(k); }
        }
        return next;
      });
    } catch { /* quiet */ }
  }, []);

  useEffect(() => {
    if (!isLead) return undefined;
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [isLead, refresh]);

  const act = async (d, path, body) => {
    const k = keyOf(d);
    setBusy(k);
    try {
      const res = await fetch(`${API_BASE}/api/assignments/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ platform: d.platform, scheduled_at: d.scheduled_at, ...body }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'That didn’t go through — try again');
      }
    } catch { setError('Can’t reach PULSE — try again'); }
    setBusy(null);
    refresh();
  };

  const step = (d, delta) => {
    const k = keyOf(d);
    setSnoozeMin((prev) => ({ ...prev, [k]: Math.min(60, Math.max(5, (prev[k] || defaultSnooze) + delta)) }));
  };

  if (!isLead || !due.length) {
    return (
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')}>
        <Alert severity="warning" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    );
  }

  return (
    <>
      {/* must pick an action — backdrop/Esc deliberately do nothing */}
      <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown onClose={() => {}}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <LocalShippingIcon color="warning" />
          {due.length === 1 ? 'Truck departure due' : `${due.length} truck departures due`}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {due.map((d, i) => {
            const k = keyOf(d);
            const mins = snoozeMin[k] || defaultSnooze;
            return (
              <Box key={k} sx={{ py: 1.5 }}>
                {i > 0 && <Divider sx={{ mb: 1.5 }} />}
                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                  {d.platform} · scheduled {timeET(d.scheduled_at)} ET
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {d.open_obds} OBD{d.open_obds === 1 ? '' : 's'} · {d.open_lines} lines · {d.open_units} units still not picked
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  PULSE will mark this departed automatically at {timeET(d.auto_depart_at)} ET
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Button variant="contained" color="warning" disabled={busy === k}
                    onClick={() => act(d, 'departures/mark', {})}>
                    Mark departed
                  </Button>
                  <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #E0E3E8', borderRadius: 1 }}>
                    <IconButton size="small" aria-label="snooze less" disabled={mins <= 5} onClick={() => step(d, -5)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Button disabled={busy === k} onClick={() => act(d, 'departure-popup/snooze', { minutes: mins })}
                      sx={{ minWidth: 130, textTransform: 'none' }}>
                      Snooze {mins} min
                    </Button>
                    <IconButton size="small" aria-label="snooze more" disabled={mins >= 60} onClick={() => step(d, 5)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Button color="inherit" disabled={busy === k}
                    onClick={() => act(d, 'departure-popup/dismiss', {})}>
                    Dismiss
                  </Button>
                </Box>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')}>
        <Alert severity="warning" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </>
  );
}
